import { Dialog, DialogContent } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import { UploadCsvStatusEnum, UploadStepsEnum } from '../../../constants/addressBook';
import { ConfirmationModal, ModalFooter, ModalHeader, StepContent, StepperNav, StepToast } from './components';
import { useUploadModal } from './UploadModalContext';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

const UploadAddressesCSVModalContent: React.FC<IProps> = ({ isOpen, onClose }) => {
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const {
    currentStep,
    visitedStep,
    fileCSV,
    status,
    dataAddresses,
    amountOfIssues,
    invalidRows,
    showIssuesOnly,
    hasRows,
    isLoading,
    isImportBlocked,
    profile,
    handleStepClickButton,
    handleNextStep,
    handleBackStep,
    handleShowIssuesOnly,
  } = useUploadModal();

  const invalidRowsCount = useMemo(() => Object.keys(invalidRows).length, [invalidRows]);

  const hasUploadedFile = useMemo(() => Boolean(fileCSV && status === UploadCsvStatusEnum.Complete), [fileCSV, status]);

  const hasFile = useMemo(
    () => currentStep === UploadStepsEnum.Upload && hasUploadedFile,
    [currentStep, hasUploadedFile],
  );

  const hasUnmappedColumns = useMemo(
    () => Boolean(currentStep === UploadStepsEnum.Map && amountOfIssues > 0),
    [currentStep, amountOfIssues],
  );

  const isRepairStep = useMemo(() => currentStep === UploadStepsEnum.Repair, [currentStep]);
  const isImportStep = useMemo(() => currentStep === UploadStepsEnum.Import, [currentStep]);

  const hasInvalidRows = useMemo(() => invalidRowsCount > 0, [invalidRowsCount]);

  const isNextStepButtonDisabled = useMemo(() => {
    switch (currentStep) {
      case UploadStepsEnum.Upload:
        return !hasFile;
      case UploadStepsEnum.Map:
        return hasUnmappedColumns;
      case UploadStepsEnum.Repair:
        return hasInvalidRows || !hasRows;
      case UploadStepsEnum.Import:
        return isImportBlocked;
      default:
        return false;
    }
  }, [currentStep, hasFile, hasUnmappedColumns, hasInvalidRows, hasRows, isImportBlocked]);

  const isCancelButtonVisible = useMemo(
    () => !(currentStep === UploadStepsEnum.Import && !isLoading),
    [currentStep, isLoading],
  );

  const isBackStepButtonVisible = useMemo(
    () => currentStep !== UploadStepsEnum.Upload && currentStep !== UploadStepsEnum.Import,
    [currentStep],
  );

  const handleClose = useCallback(() => {
    if (isImportStep || !hasUploadedFile) {
      onClose();
      return;
    }
    setConfirmationModalOpen(true);
  }, [isImportStep, hasUploadedFile, onClose]);

  const handleKeepEditing = useCallback(() => {
    setConfirmationModalOpen(false);
  }, []);

  const handleDiscardUpload = useCallback(() => {
    setConfirmationModalOpen(false);
    onClose();
  }, [onClose]);

  const stepTitle = useMemo(() => profile.steps[currentStep - 1].title, [currentStep, profile]);

  return (
    <>
      <Dialog
        open={isOpen}
        sx={{
          '& .MuiDialog-paper': {
            width: 1200,
            height: 865,
            borderRadius: 0.5,
          },
        }}
        onClose={handleClose}
        maxWidth={false}
        disableEscapeKeyDown
      >
        <ModalHeader title={stepTitle} onClose={handleClose} />
        <DialogContent sx={{ py: 0, display: 'flex', flexDirection: 'column' }}>
          <StepperNav
            currentStep={currentStep}
            visitedStep={visitedStep}
            onStepClick={handleStepClickButton}
            isNextStepDisabled={isNextStepButtonDisabled}
            steps={profile.steps}
          />
          <StepContent currentStep={currentStep} dataAddresses={dataAddresses} onClose={handleClose} />
        </DialogContent>
        <ModalFooter
          onClose={handleClose}
          isCancelButtonVisible={isCancelButtonVisible}
          isBackButtonVisible={isBackStepButtonVisible}
          onBack={handleBackStep}
          isImportStep={isImportStep}
          onNext={handleNextStep}
          isNextButtonDisabled={isNextStepButtonDisabled}
          toast={
            <StepToast
              hasUnmappedColumns={hasUnmappedColumns}
              amountOfIssues={amountOfIssues}
              hasInvalidRows={hasInvalidRows}
              hasRows={hasRows}
              invalidRowsCount={invalidRowsCount}
              isRepairStep={isRepairStep}
              onShowIssuesOnly={handleShowIssuesOnly}
              showIssuesOnly={showIssuesOnly}
            />
          }
        />
      </Dialog>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onDiscardUpload={handleDiscardUpload}
        onCloseConfirmationModal={handleKeepEditing}
      />
    </>
  );
};

export default UploadAddressesCSVModalContent;
